import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    MenuItem,
    TextField,
    Stack,
    Chip,
    CircularProgress,
    Alert,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { Download as DownloadIcon, TableChart as ExcelIcon, Feedback as FeedbackIcon } from "@mui/icons-material";
import {
    DataGrid,
    GridColDef,
    GridToolbarQuickFilter,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { apiService } from "../services/api";
import { SiteFormMailingSetting, SiteFormRecord } from "../types";
import { exportToExcel } from "../utils/excelExport";
import Constants from "../constants";
import { authStore } from "../stores";

const NULLISH_STRINGS = new Set(["", "undefined", "null", "nan"]);
const AIRPORT_KEYS = new Set(["city_from", "city_to", "city_start", "city_finish", "city_layover"]);

function normalizeValue(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    const text = String(value);
    const normalized = text.trim().toLowerCase();
    return NULLISH_STRINGS.has(normalized) ? "" : text;
}

function withFallback(value: string, fallback: string): string {
    return value || fallback;
}

function getFlightNumber(payload: Record<string, unknown>): string {
    const direct = normalizeValue(payload.flight_number);
    const alt = normalizeValue(payload.flight_number_u);
    return withFallback(direct || alt, "Рейс не указан");
}

function getFlightDate(payload: Record<string, unknown>): string {
    const direct = normalizeValue(payload.flight_date);
    const alt = normalizeValue(payload.flight_date_u);
    return withFallback(direct || alt, "-");
}

function CustomToolbar() {
    return (
        <GridToolbarContainer sx={{ gap: 1, py: 1, px: 1, flexWrap: "wrap" }}>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <Box sx={{ ml: "auto", minWidth: 220 }}>
                <GridToolbarQuickFilter debounceMs={300} />
            </Box>
        </GridToolbarContainer>
    );
}

function flattenForExport(rows: SiteFormRecord[]): { headers: string[]; data: string[][] } {
    const keySet = new Set<string>();
    const flatObjects = rows.map((r) => {
        const o: Record<string, string> = {
            id: String(r.id),
            formType: r.formType,
            deviceHostname: r.deviceHostname,
            fileTimestamp: String(r.fileTimestamp),
            sourceFilename: r.sourceFilename ?? "",
            companyName: r.companyName ?? "",
            aboxSerial: r.aboxSerial ?? "",
            createdAt: r.createdAt ? new Date(r.createdAt).toISOString() : "",
        };
        const p = r.payload || {};
        for (const [k, v] of Object.entries(p)) {
            let normalized = normalizeValue(v);
            if (k === "flight_number" || k === "flight_number_u") {
                normalized = withFallback(normalized, "Рейс не указан");
            } else if (k === "flight_date" || k === "flight_date_u") {
                normalized = withFallback(normalized, "-");
            } else if (AIRPORT_KEYS.has(k)) {
                normalized = withFallback(normalized, "-");
            }
            o[`payload_${k}`] = normalized;
        }
        Object.keys(o).forEach((k) => keySet.add(k));
        return o;
    });
    const headers = Array.from(keySet);
    const data = flatObjects.map((o) => headers.map((h) => o[h] ?? ""));
    return { headers, data };
}

type MailingFormType = "feedback" | "landing" | "logbook";
type MailingUiSetting = SiteFormMailingSetting & { recipientsText: string };
const MAILING_FORM_TYPES: MailingFormType[] = ["feedback", "landing", "logbook"];

const SiteFormsPage: React.FC = () => {
    const theme = useTheme();
    const [rows, setRows] = useState<SiteFormRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formType, setFormType] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [mailingSettings, setMailingSettings] = useState<Record<MailingFormType, MailingUiSetting> | null>(null);
    const [mailingLoading, setMailingLoading] = useState(false);
    const [mailingError, setMailingError] = useState<string | null>(null);
    const [mailingInfo, setMailingInfo] = useState<string | null>(null);
    const [savingFormType, setSavingFormType] = useState<MailingFormType | null>(null);
    const [testingFormType, setTestingFormType] = useState<MailingFormType | null>(null);

    const isAdmin = (() => {
        const user = authStore.user;
        if (!user) return false;
        return user.role === "admin" || (user.supplierId == null && user.clientId == null);
    })();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { items } = await apiService.getSiteForms({
                formType: formType || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
            });
            setRows(items);
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || "Не удалось загрузить формы");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [formType, startDate, endDate]);

    useEffect(() => {
        void load();
    }, [load]);

    const loadMailingSettings = useCallback(async () => {
        if (!isAdmin) return;
        setMailingLoading(true);
        setMailingError(null);
        try {
            const settings = await apiService.getAuroraSiteFormMailingSettings();
            const next: Record<MailingFormType, MailingUiSetting> = {
                feedback: {
                    formType: "feedback",
                    recipients: [],
                    recipientsText: "",
                    fridayTime: "10:00",
                    scheduleType: "friday",
                    timezone: "Europe/Moscow",
                    isEnabled: false,
                    lastAutoSentAt: null,
                },
                landing: {
                    formType: "landing",
                    recipients: [],
                    recipientsText: "",
                    fridayTime: "10:00",
                    scheduleType: "friday",
                    timezone: "Europe/Moscow",
                    isEnabled: false,
                    lastAutoSentAt: null,
                },
                logbook: {
                    formType: "logbook",
                    recipients: [],
                    recipientsText: "",
                    fridayTime: "10:00",
                    scheduleType: "friday",
                    timezone: "Europe/Moscow",
                    isEnabled: false,
                    lastAutoSentAt: null,
                },
            };

            for (const item of settings) {
                if (!MAILING_FORM_TYPES.includes(item.formType)) continue;
                next[item.formType] = {
                    ...item,
                    recipientsText: (item.recipients || []).join(", "),
                };
            }
            setMailingSettings(next);
        } catch (e: any) {
            setMailingError(e?.response?.data?.message || e?.message || "Не удалось загрузить настройки рассылки");
            setMailingSettings(null);
        } finally {
            setMailingLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (!isAdmin) return;
        void loadMailingSettings();
    }, [isAdmin, loadMailingSettings]);

    const gridRows = useMemo(
        () =>
            rows.map((r) => {
                const p = (r.payload || {}) as Record<string, unknown>;
                return {
                    id: r.id,
                    formType: r.formType,
                    deviceHostname: r.deviceHostname,
                    fileTs: r.fileTimestamp,
                    fileTimeLabel: new Date(r.fileTimestamp * 1000).toLocaleString("ru-RU"),
                    aboxSerial: normalizeValue(r.aboxSerial),
                    companyName: normalizeValue(r.companyName),
                    createdLabel: r.createdAt ? new Date(r.createdAt).toLocaleString("ru-RU") : "",
                    flightNumber: getFlightNumber(p),
                    flightDate: getFlightDate(p),
                    email: normalizeValue(p.email),
                    phone: normalizeValue(p.phone),
                    feedback: normalizeValue(p.feedback),
                    rating: normalizeValue(p.rating),
                    sex: normalizeValue(p.sex),
                    age: normalizeValue(p.age),
                    sourceFilename: normalizeValue(r.sourceFilename),
                };
            }),
        [rows],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "formType",
                headerName: "Форма",
                width: 110,
                renderCell: (params) => (
                    <Chip
                        size="small"
                        label={params.value as string}
                        color={
                            params.value === "feedback"
                                ? "primary"
                                : params.value === "landing"
                                  ? "secondary"
                                  : "info"
                        }
                    />
                ),
            },
            { field: "deviceHostname", headerName: "Hostname", flex: 0.8, minWidth: 110 },
            { field: "fileTimeLabel", headerName: "Время (файл)", flex: 1, minWidth: 160 },
            { field: "aboxSerial", headerName: "Борт", flex: 0.7, minWidth: 100 },
            { field: "companyName", headerName: "Компания", flex: 0.7, minWidth: 100 },
            { field: "flightNumber", headerName: "Рейс", flex: 0.6, minWidth: 90 },
            { field: "flightDate", headerName: "Дата рейса", flex: 1, minWidth: 140 },
            { field: "email", headerName: "Email", flex: 1, minWidth: 160 },
            { field: "phone", headerName: "Телефон", flex: 0.8, minWidth: 120 },
            { field: "rating", headerName: "Рейтинг", width: 90 },
            { field: "sex", headerName: "Пол", width: 90 },
            { field: "age", headerName: "Возраст", width: 100 },
            {
                field: "feedback",
                headerName: "Комментарий",
                flex: 1.2,
                minWidth: 200,
            },
            { field: "createdLabel", headerName: "Импорт", flex: 1, minWidth: 150 },
            { field: "sourceFilename", headerName: "Файл", flex: 1, minWidth: 220 },
        ],
        [],
    );

    const handleExportCsv = () => {
        const { headers, data } = flattenForExport(rows);
        const csvContent = [
            headers.join(Constants.CSV_SEPARATOR),
            ...data.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(Constants.CSV_SEPARATOR)),
        ].join("\n");
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `site_forms_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handleExportXlsx = () => {
        const { headers, data } = flattenForExport(rows);
        exportToExcel({
            filename: `site_forms_${new Date().toISOString().split("T")[0]}`,
            sheetName: "Формы",
            headers,
            data,
            additionalInfo: [
                { label: "Записей", value: rows.length },
                { label: "Фильтр типа", value: formType || "все" },
                { label: "Период создания (импорт)", value: `${startDate || "—"} — ${endDate || "—"}` },
            ],
        });
    };

    const validateRecipients = (value: string): string[] | null => {
        const recipients = value
            .split(/[,\n;]/)
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!recipients.every((email) => emailRegex.test(email))) {
            return null;
        }
        return recipients;
    };

    const updateUiSetting = (formTypeKey: MailingFormType, patch: Partial<MailingUiSetting>) => {
        setMailingSettings((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [formTypeKey]: {
                    ...prev[formTypeKey],
                    ...patch,
                },
            };
        });
    };

    const handleSaveMailingSetting = async (formTypeKey: MailingFormType) => {
        if (!mailingSettings) return;
        const setting = mailingSettings[formTypeKey];
        const recipients = validateRecipients(setting.recipientsText);
        if (recipients === null) {
            setMailingError(`Некорректные email в ${formTypeKey}`);
            setMailingInfo(null);
            return;
        }
        if (setting.isEnabled && recipients.length === 0) {
            setMailingError(`Для включенной рассылки ${formTypeKey} нужен хотя бы один email`);
            setMailingInfo(null);
            return;
        }
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(setting.fridayTime)) {
            setMailingError(`Некорректное время в ${formTypeKey}. Формат: HH:mm`);
            setMailingInfo(null);
            return;
        }

        setSavingFormType(formTypeKey);
        setMailingError(null);
        setMailingInfo(null);
        try {
            const saved = await apiService.updateAuroraSiteFormMailingSetting(formTypeKey, {
                recipients,
                fridayTime: setting.fridayTime,
                isEnabled: setting.isEnabled,
                scheduleType: setting.scheduleType,
            });
            updateUiSetting(formTypeKey, {
                ...saved,
                recipientsText: (saved.recipients || []).join(", "),
            });
            setMailingInfo(`Настройки ${formTypeKey} сохранены`);
        } catch (e: any) {
            setMailingError(e?.response?.data?.message || e?.message || `Не удалось сохранить ${formTypeKey}`);
        } finally {
            setSavingFormType(null);
        }
    };

    const handleTestSend = async (formTypeKey: MailingFormType) => {
        setTestingFormType(formTypeKey);
        setMailingError(null);
        setMailingInfo(null);
        try {
            await apiService.testSendAuroraSiteFormMailing(formTypeKey);
            setMailingInfo(`Тестовая отправка ${formTypeKey} выполнена`);
        } catch (e: any) {
            setMailingError(e?.response?.data?.message || e?.message || `Ошибка тестовой отправки ${formTypeKey}`);
        } finally {
            setTestingFormType(null);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                background: `linear-gradient(165deg, ${theme.palette.background.default} 0%, #121a1e 45%, ${theme.palette.background.default} 100%)`,
                pb: 4,
            }}
        >
            <Box
                sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(255,64,129,0.06) 100%)`,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    <FeedbackIcon sx={{ fontSize: 40, color: "primary.main", opacity: 0.9 }} />
                    <Box flex={1}>
                        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
                            Формы обратной связи
                        </Typography>
                        <Typography variant="body2" color="text.secondary" maxWidth={720}>
                            Данные с бортов (feedback, landing, logbook). Используйте панель: быстрый поиск, кнопка
                            «Столбцы», «Фильтры» и меню столбца для сортировки. Выгрузка учитывает отфильтрованные на
                            сервере записи; для точной выборки уточните период импорта.
                        </Typography>
                    </Box>
                    <Stack direction="row" gap={1}>
                        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
                            CSV
                        </Button>
                        <Button variant="contained" color="success" startIcon={<ExcelIcon />} onClick={handleExportXlsx}>
                            Excel
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {isAdmin && (
                <Card
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                    }}
                >
                    <CardContent>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                            Настройки рассылки Аврора
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Авторассылка в таймзоне Europe/Moscow (режим: пятница или ежедневно). По каждой форме отправляется отдельное письмо с XLSX.
                        </Typography>

                        {mailingError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {mailingError}
                            </Alert>
                        )}
                        {mailingInfo && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {mailingInfo}
                            </Alert>
                        )}

                        {mailingLoading || !mailingSettings ? (
                            <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                {MAILING_FORM_TYPES.map((type) => {
                                    const setting = mailingSettings[type];
                                    return (
                                        <Card key={type} variant="outlined" sx={{ borderColor: theme.palette.divider }}>
                                            <CardContent>
                                                <Stack
                                                    direction={{ xs: "column", md: "row" }}
                                                    spacing={2}
                                                    alignItems={{ xs: "stretch", md: "center" }}
                                                >
                                                    <Chip label={type} color="primary" sx={{ width: "fit-content" }} />
                                                    <TextField
                                                        label="Получатели (через запятую)"
                                                        value={setting.recipientsText}
                                                        onChange={(e) =>
                                                            updateUiSetting(type, {
                                                                recipientsText: e.target.value,
                                                            })
                                                        }
                                                        size="small"
                                                        sx={{ minWidth: 320, flex: 1 }}
                                                        placeholder="mail1@example.com, mail2@example.com"
                                                    />
                                                    <TextField
                                                        select
                                                        label="Расписание"
                                                        value={setting.scheduleType}
                                                        onChange={(e) =>
                                                            updateUiSetting(type, {
                                                                scheduleType: e.target.value as "friday" | "daily",
                                                            })
                                                        }
                                                        size="small"
                                                        sx={{ minWidth: 150 }}
                                                    >
                                                        <MenuItem value="friday">Пятница</MenuItem>
                                                        <MenuItem value="daily">Ежедневно</MenuItem>
                                                    </TextField>
                                                    <TextField
                                                        label="Время отправки"
                                                        type="time"
                                                        value={setting.fridayTime}
                                                        onChange={(e) =>
                                                            updateUiSetting(type, {
                                                                fridayTime: e.target.value,
                                                            })
                                                        }
                                                        size="small"
                                                        sx={{ minWidth: 150 }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={setting.isEnabled}
                                                                onChange={(e) =>
                                                                    updateUiSetting(type, {
                                                                        isEnabled: e.target.checked,
                                                                    })
                                                                }
                                                            />
                                                        }
                                                        label="Активна"
                                                    />
                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => void handleSaveMailingSetting(type)}
                                                            disabled={savingFormType === type || testingFormType === type}
                                                        >
                                                            {savingFormType === type ? "Сохранение..." : "Сохранить"}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => void handleTestSend(type)}
                                                            disabled={testingFormType === type || savingFormType === type}
                                                        >
                                                            {testingFormType === type ? "Отправка..." : "Тестовая отправка"}
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                                    Режим: {setting.scheduleType === "daily" ? "Ежедневно" : "Пятница"}.
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                    Последняя автоотправка:{" "}
                                                    {setting.lastAutoSentAt
                                                        ? new Date(setting.lastAutoSentAt).toLocaleString("ru-RU")
                                                        : "ещё не выполнялась"}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                }}
            >
                <CardContent sx={{ pb: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        sx={{ mb: 2 }}
                    >
                        <TextField
                            select
                            label="Тип формы"
                            value={formType}
                            onChange={(e) => setFormType(e.target.value)}
                            sx={{ minWidth: 180 }}
                            size="small"
                        >
                            <MenuItem value="">Все</MenuItem>
                            <MenuItem value="feedback">feedback</MenuItem>
                            <MenuItem value="landing">landing</MenuItem>
                            <MenuItem value="logbook">logbook</MenuItem>
                        </TextField>
                        <TextField
                            label="Импорт с"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 160 }}
                        />
                        <TextField
                            label="Импорт по"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 160 }}
                        />
                        <Button variant="outlined" onClick={() => void load()} disabled={loading}>
                            Обновить
                        </Button>
                    </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && rows.length === 0 ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ width: "100%", height: 640 }}>
                            <DataGrid
                                rows={gridRows}
                                columns={columns}
                                disableRowSelectionOnClick
                                pageSizeOptions={[10, 25, 50, 100]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 25 } },
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                sx={{
                                    border: "none",
                                    "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "rgba(0,188,212,0.08)",
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    },
                                    "& .MuiDataGrid-cell": { borderColor: theme.palette.divider },
                                    "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "rgba(0,188,212,0.06)",
                                    },
                                }}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default SiteFormsPage;
